import { useEffect, useRef, useState } from 'react'
import { Button, Empty, Input, Popconfirm, Popover, Select, Spin, Switch, Tag, Tooltip } from 'antd'
import {
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  EditOutlined,
  HistoryOutlined,
  MessageOutlined,
  PlusOutlined,
  RightOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
  StopOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import {
  useAgentBuilderCreateMemoryMutation,
  useAgentBuilderDashboardVersionsQuery,
  useAgentBuilderDeleteDashboardVersionMutation,
  useAgentBuilderDeleteMemoryMutation,
  useAgentBuilderMemoriesQuery,
  useAgentBuilderPublishDashboardVersionMutation,
  useAgentBuilderResolveScheduleConfirmationMutation,
  useAgentBuilderRestoreDashboardVersionMutation,
  useAgentBuilderSendChatMessageMutation,
  useAgentBuilderUnpublishDashboardVersionMutation,
  useAgentBuilderUpdateMemoryMutation,
} from '../hooks'
import { getAgentBuilderChatMessage, type AgentBuilderChatResult } from '../api'
import { useToast } from '@/contexts/ToastContext'
import { canCreateAgentBuilderDashboardDraft, canCreateOrModifyAgentBuilderDashboards } from '@/features/auth/permissions'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useAuthSession } from '@/lib/session'
import { normalizeDashboardDefinition, validateDashboard } from '../builderCore'
import { formatMetricValue, resolveColumnFormat } from '../vizUtils'
import { formatFieldValue, type FieldFormat } from '@/lib/fieldFormat'
import { UserTypes } from '@/types/authorization'
import type {
  AgentBuilderChatMessage,
  AgentBuilderDashboardVersion,
  AgentBuilderMemory,
  AgentBuilderMemoryDashboardScope,
  AgentBuilderMemoryUserScope,
  DashboardRuntimeState,
  DashboardCatalogPlatform,
  DashboardDefinition,
  DashboardWidget,
} from '../types'
import { buildDashboardAnalysisContext, createEmptyDashboardRuntimeState } from '../runtime'
import { sanitizeDashboardSection } from '../sections'
import { ScheduleImpactConfirmCard } from './ScheduleImpactConfirmCard'

const { TextArea } = Input
const APPLIED_DASHBOARD_MESSAGE = "Done — I've updated the dashboard."
const REJECTED_DASHBOARD_MESSAGE = "I couldn't apply that dashboard update."
const SAVED_BUT_NOT_LOADED_MESSAGE = "The change was saved, but this view couldn't load it. Reload the page to see it."
const CHAT_RETRIEVAL_FAILURE_MESSAGE = "I couldn't retrieve that response. Please try again."
const CHAT_POLLING_TIMEOUT_MESSAGE = "I'm still working on that. Please try again shortly."
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isValidGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_PATTERN.test(value)
}

export type AgentBuilderSavedDashboardMeta = {
  rootDashboardId: string | null
  dashboardId: string | null
  versionId: string | null
}

function formatVersionTimestamp(value: string | null) {
  if (!value) return 'Unknown time'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function formatChatMetricValue(value: unknown, metricName?: string | null, format?: FieldFormat | null) {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number' || typeof value === 'string') {
    return format ? formatFieldValue(value, format) : formatMetricValue(value, metricName)
  }
  return String(value)
}

function dashboardFromAgentUpdate(
  rawUpdate: unknown,
  currentDashboard: DashboardDefinition | null,
): DashboardDefinition | null {
  if (rawUpdate && typeof rawUpdate === 'object' && !Array.isArray(rawUpdate)) {
    return rawUpdate as DashboardDefinition
  }

  if (Array.isArray(rawUpdate) && currentDashboard) {
    return {
      ...currentDashboard,
      widgets: rawUpdate as DashboardWidget[],
      warnings: currentDashboard.warnings ?? [],
      dashboardFilters: currentDashboard.dashboardFilters ?? [],
    }
  }

  return null
}

interface AgentBuilderPanelProps {
  clientId: string
  dashboardId?: string | null
  currentDashboard: DashboardDefinition | null
  catalog: DashboardCatalogPlatform[] | null
  platformTypeId: number
  onDashboardUpdated: (updated: DashboardDefinition, prompt?: string, savedDashboard?: AgentBuilderSavedDashboardMeta) => boolean
  onDashboardRestored: (updated: DashboardDefinition, version: AgentBuilderDashboardVersion) => boolean
  onClose?: () => void
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  messages: AgentBuilderChatMessage[]
  onMessagesChange: (messages: AgentBuilderChatMessage[]) => void
  busy: boolean
  onBusyChange: (busy: boolean) => void
  runtimeState?: DashboardRuntimeState
  slicerOptionsByField?: Record<string, string[]>
}

export function AgentBuilderPanel({
  clientId,
  dashboardId,
  currentDashboard,
  catalog,
  platformTypeId,
  onDashboardUpdated,
  onDashboardRestored,
  onClose,
  expanded = false,
  onExpandedChange,
  messages,
  onMessagesChange: setMessages,
  busy,
  onBusyChange,
  runtimeState,
  slicerOptionsByField,
}: AgentBuilderPanelProps) {
  const [activeChildView, setActiveChildView] = useState<'memory' | 'version-history'>('memory')
  const [childViewOpen, setChildViewOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [memoryInput, setMemoryInput] = useState('')
  const [memoryUserScope, setMemoryUserScope] = useState<AgentBuilderMemoryUserScope>('current_user')
  const [memoryDashboardScope, setMemoryDashboardScope] = useState<AgentBuilderMemoryDashboardScope>(
    dashboardId ? 'this_dashboard' : 'all_dashboards',
  )
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null)
  const [editingMemoryText, setEditingMemoryText] = useState('')
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null)
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null)
  const [publishingVersionId, setPublishingVersionId] = useState<string | null>(null)
  const [unpublishingVersionId, setUnpublishingVersionId] = useState<string | null>(null)
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(null)
  const [saveChatModificationsAsDraft, setSaveChatModificationsAsDraft] = useState(true)
  const { addToast } = useToast()
  const authSession = useAuthSession()
  const rootDashboardId = dashboardId || null
  const isDiAdmin = canCreateAgentBuilderDashboardDraft(authSession)
  const { featureFlags } = useAuthorization()
  const canModifyDashboards = canCreateOrModifyAgentBuilderDashboards(authSession, featureFlags)
  const canManageClientMemory =
    authSession?.currentUserTypeId === UserTypes.Admin || authSession?.currentUserTypeId === UserTypes.ClientAdmin
  const sendMutation = useAgentBuilderSendChatMessageMutation()
  const restoreMutation = useAgentBuilderRestoreDashboardVersionMutation(clientId, dashboardId)
  const publishMutation = useAgentBuilderPublishDashboardVersionMutation(clientId, dashboardId)
  const unpublishMutation = useAgentBuilderUnpublishDashboardVersionMutation(clientId, dashboardId)
  const deleteVersionMutation = useAgentBuilderDeleteDashboardVersionMutation(clientId, dashboardId)
  const versionsQuery = useAgentBuilderDashboardVersionsQuery(clientId, dashboardId)
  const memoriesQuery = useAgentBuilderMemoriesQuery(clientId, rootDashboardId)
  const createMemoryMutation = useAgentBuilderCreateMemoryMutation(clientId, rootDashboardId)
  const updateMemoryMutation = useAgentBuilderUpdateMemoryMutation(clientId, rootDashboardId)
  const deleteMemoryMutation = useAgentBuilderDeleteMemoryMutation(clientId, rootDashboardId)
  const scheduleConfirmationMutation = useAgentBuilderResolveScheduleConfirmationMutation(clientId, rootDashboardId)
  const [resolvingScheduleConfirmationId, setResolvingScheduleConfirmationId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef(messages)
  const activePollRef = useRef<{ chatId: string; runId: number } | null>(null)
  const pollRunIdRef = useRef(0)
  messagesRef.current = messages

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    setCurrentVersionId(null)
  }, [dashboardId])

  useEffect(() => {
    if (!rootDashboardId && memoryDashboardScope === 'this_dashboard') {
      setMemoryDashboardScope('all_dashboards')
    }
  }, [memoryDashboardScope, rootDashboardId])

  const chatBusy = sendMutation.isPending || busy

  // Validates and applies an agent dashboard update. Returns the rejection reason, or null once applied.
  function applyDashboardUpdate(response: AgentBuilderChatResult, question: string): string | null {
    try {
      const rawUpdatedDashboard = dashboardFromAgentUpdate(
        JSON.parse(response.updatedDashboardJson ?? ''),
        currentDashboard,
      )

      if (!rawUpdatedDashboard || !Array.isArray(rawUpdatedDashboard.widgets)) {
        return 'The agent response did not include dashboard widgets.'
      }

      if (!catalog) {
        return 'The dashboard catalog was unavailable for validation.'
      }

      const rawValidation = validateDashboard(rawUpdatedDashboard, catalog)
      if (!rawValidation.ok) {
        return rawValidation.errors.join('; ')
      }

      const updatedDashboard = normalizeDashboardDefinition(rawUpdatedDashboard, catalog)
      const validation = validateDashboard(updatedDashboard, catalog)
      if (!validation.ok) {
        return validation.errors.join('; ')
      }

      if (!onDashboardUpdated(updatedDashboard, question, {
        rootDashboardId: response.rootDashboardId,
        dashboardId: response.dashboardId,
        versionId: response.versionId,
      })) {
        return 'Local dashboard validation rejected the update.'
      }

      return null
    } catch {
      return 'The agent returned invalid JSON.'
    }
  }

  function applyAgentResult(response: AgentBuilderChatResult, nextMessages: AgentBuilderChatMessage[], question: string) {
    // A pending schedule confirmation also arrives without updatedDashboardJson; its card renders from the message.
    if (!response.updatedDashboardJson) {
      setMessages([...nextMessages, response.message])
      return
    }

    const rejection = applyDashboardUpdate(response, question)
    setMessages([
      ...nextMessages,
      { ...response.message, content: rejection ? REJECTED_DASHBOARD_MESSAGE : APPLIED_DASHBOARD_MESSAGE },
    ])
    if (rejection) {
      addToast(`Dashboard update rejected: ${rejection}`, 'error')
    }
  }

  function isPollCurrent(runId: number, userMessageId: string) {
    const lastMessage = messagesRef.current.at(-1)
    return pollRunIdRef.current === runId
      && lastMessage?.role === 'user'
      && lastMessage.messageId === userMessageId
  }

  function appendPollingFailure(
    nextMessages: AgentBuilderChatMessage[],
    content: string,
    toastMessage = content,
  ) {
    setMessages([
      ...nextMessages,
      { messageId: `msg-${Date.now()}-e`, role: 'assistant', content },
    ])
    addToast(toastMessage, 'error')
  }

  async function pollAgentBuilderChat(
    agentBuilderChatId: string,
    nextMessages: AgentBuilderChatMessage[],
    question: string,
    runId: number,
  ) {
    const userMessageId = nextMessages.at(-1)?.messageId
    if (!userMessageId) return

    onBusyChange(true)
    try {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (!isPollCurrent(runId, userMessageId)) return

        try {
          const status = await getAgentBuilderChatMessage(agentBuilderChatId)
          if (!isPollCurrent(runId, userMessageId)) return

          if (status.status === 'completed') {
            if (!status.result || !isValidGuid(status.result.message.messageId)) {
              appendPollingFailure(nextMessages, CHAT_RETRIEVAL_FAILURE_MESSAGE)
              return
            }
            applyAgentResult(status.result, nextMessages, question)
            return
          }

          if (status.status === 'failed') {
            const failureMessage = status.errorMessage || 'Agent Builder chat failed.'
            appendPollingFailure(nextMessages, failureMessage)
            return
          }
        } catch {
          // A single request failure is not terminal. Retry until expiry.
        }

        if (attempt < 89) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
        }
      }

      if (isPollCurrent(runId, userMessageId)) {
        appendPollingFailure(nextMessages, CHAT_POLLING_TIMEOUT_MESSAGE)
      }
    } finally {
      if (pollRunIdRef.current === runId) {
        activePollRef.current = null
        onBusyChange(false)
      }
    }
  }

  useEffect(() => {
    const lastMessage = messages.at(-1)
    if (!lastMessage || lastMessage.role !== 'user' || sendMutation.isPending) return

    const chatId = lastMessage.agentBuilderChatId
    if (!isValidGuid(chatId)) {
      appendPollingFailure(messages, CHAT_RETRIEVAL_FAILURE_MESSAGE)
      return
    }

    if (activePollRef.current?.chatId === chatId) return

    const runId = pollRunIdRef.current + 1
    pollRunIdRef.current = runId
    activePollRef.current = { chatId, runId }
    void pollAgentBuilderChat(chatId, messages, lastMessage.content, runId)

    return () => {
      if (pollRunIdRef.current === runId) {
        pollRunIdRef.current += 1
        activePollRef.current = null
        onBusyChange(false)
      }
    }
    // Poll ownership is intentionally keyed only by persisted history and the
    // send transition; callback identities must not restart an active request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sendMutation.isPending])

  async function handleScheduleConfirmation(messageId: string, chatId: string, accept: boolean) {
    setResolvingScheduleConfirmationId(messageId)
    try {
      const status = await scheduleConfirmationMutation.mutateAsync({ chatId, accept })
      const result = status.result
      if (!result) {
        throw new Error('The schedule confirmation could not be resolved.')
      }

      const current = messagesRef.current
      const index = current.findIndex((m) => m.role === 'assistant' && m.messageId === messageId)
      if (index === -1) return

      const question = current.slice(0, index).reverse().find((m) => m.role === 'user')?.content ?? ''
      const rejection = result.updatedDashboardJson ? applyDashboardUpdate(result, question) : null
      if (rejection) {
        addToast(`Dashboard update rejected: ${rejection}`, 'error')
      }

      // The server's outcome text (e.g. how many schedules were deleted) replaces the question. The server
      // has already committed by now, so a local rejection only adds a notice; it never hides that outcome.
      const outcome = result.message.content || current[index]?.content || ''
      setMessages(current.map((m, i) => (i === index
        ? {
            ...m,
            content: rejection ? `${outcome} ${SAVED_BUT_NOT_LOADED_MESSAGE}` : outcome,
            pendingScheduleConfirmation: result.message.pendingScheduleConfirmation ?? m.pendingScheduleConfirmation,
          }
        : m)))
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to resolve the schedule confirmation.', 'error')
    } finally {
      setResolvingScheduleConfirmationId(null)
    }
  }

  function handleSend() {
    const question = input.trim()
    if (!question || chatBusy || !clientId) return

    const agentBuilderChatId = crypto.randomUUID()
    const userMessage: AgentBuilderChatMessage = {
      messageId: `msg-${Date.now()}-u`,
      agentBuilderChatId,
      role: 'user',
      content: question,
    }

    const nextMessages = [...messages, userMessage]
    setMessages(nextMessages)
    setInput('')

    const requestDashboard = currentDashboard
      ? {
          ...currentDashboard,
          section: sanitizeDashboardSection(currentDashboard.section, platformTypeId),
        }
      : null

    let dashboardAnalysisContext = null
    try {
      dashboardAnalysisContext = requestDashboard
        ? buildDashboardAnalysisContext(
            requestDashboard,
            runtimeState ?? createEmptyDashboardRuntimeState(),
            slicerOptionsByField ?? {},
            catalog,
          )
        : null
    } catch {
      dashboardAnalysisContext = null
    }

    sendMutation.mutate(
      {
        agentBuilderChatId,
        currentDashboardJson: requestDashboard ? JSON.stringify(requestDashboard) : '',
        dashboardAnalysisContext,
        question,
        messages: nextMessages,
        clientId,
        dashboardId,
        platformTypeId,
        section: requestDashboard?.section ?? null,
        isDraft: isDiAdmin && saveChatModificationsAsDraft,
      },
      {
        onSuccess: (response) => {
          if (!isValidGuid(response.agentBuilderChatId) || response.agentBuilderChatId !== agentBuilderChatId) {
            const failureMessage: AgentBuilderChatMessage = {
              messageId: `msg-${Date.now()}-e`,
              role: 'assistant',
              content: 'I could not reach Agent Builder. Please try again.',
            }
            setMessages([...nextMessages, failureMessage])
            addToast('Agent Builder returned an invalid chat id.', 'error')
            return
          }
        },
        onError: (error) => {
          const failureMessage: AgentBuilderChatMessage = {
            messageId: `msg-${Date.now()}-e`,
            role: 'assistant',
            content: 'I could not reach Agent Builder. Please try again.',
          }
          setMessages([...nextMessages, failureMessage])
          addToast(error instanceof Error ? error.message : 'Agent Builder chat failed.', 'error')
        },
      },
    )
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleClear() {
    setMessages([])
    setInput('')
    onBusyChange(false)
    sendMutation.reset()
    setSettingsOpen(false)
  }

  function openChildView(view: 'memory' | 'version-history') {
    setActiveChildView(view)
    setChildViewOpen(true)
    setSettingsOpen(false)
  }

  function handleCreateMemory() {
    const memory = memoryInput.trim()
    if (!memory || !clientId || createMemoryMutation.isPending) return
    if (memoryDashboardScope === 'this_dashboard' && !rootDashboardId) return
    if (memoryUserScope === 'entire_client' && !canManageClientMemory) return

    createMemoryMutation.mutate(
      {
        clientId,
        rootDashboardId: memoryDashboardScope === 'this_dashboard' ? rootDashboardId : null,
        userScope: memoryUserScope,
        dashboardScope: memoryDashboardScope,
        memory,
      },
      {
        onSuccess: () => {
          setMemoryInput('')
          addToast('Memory saved.', 'success')
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to save memory.', 'error')
        },
      },
    )
  }

  function handleStartEditMemory(memory: AgentBuilderMemory) {
    setEditingMemoryId(memory.id)
    setEditingMemoryText(memory.memory)
  }

  function handleCancelEditMemory() {
    setEditingMemoryId(null)
    setEditingMemoryText('')
  }

  function handleUpdateMemory(memory: AgentBuilderMemory) {
    const nextMemory = editingMemoryText.trim()
    if (!nextMemory || !clientId || updateMemoryMutation.isPending) return
    if (memory.dashboardScope === 'this_dashboard' && !rootDashboardId) return
    if (memory.userScope === 'entire_client' && !canManageClientMemory) return

    updateMemoryMutation.mutate(
      {
        clientId,
        rootDashboardId: memory.rootDashboardId,
        userScope: memory.userScope,
        dashboardScope: memory.dashboardScope,
        memoryId: memory.id,
        memory: nextMemory,
      },
      {
        onSuccess: () => {
          handleCancelEditMemory()
          addToast('Memory updated.', 'success')
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to update memory.', 'error')
        },
      },
    )
  }

  function handleDeleteMemory(memory: AgentBuilderMemory) {
    if (!clientId || deleteMemoryMutation.isPending) return
    if (memory.userScope === 'entire_client' && !canManageClientMemory) return

    deleteMemoryMutation.mutate(
      {
        clientId,
        memoryId: memory.id,
      },
      {
        onSuccess: () => {
          addToast('Memory deleted.', 'success')
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to delete memory.', 'error')
        },
      },
    )
  }

  function handleRestoreVersion(version: AgentBuilderDashboardVersion) {
    if (!version.dashboardJson) {
      addToast('This version does not contain dashboard JSON.', 'warning')
      return
    }

    restoreMutation.mutate(
      {
        clientId,
        versionId: version.versionId || version.id,
      },
      {
        onSuccess: (restoredVersion) => {
          const dashboardJson = restoredVersion.dashboardJson ?? version.dashboardJson
          if (!dashboardJson) {
            addToast('This version does not contain dashboard JSON.', 'warning')
            return
          }

          try {
            const restoredDashboard = JSON.parse(dashboardJson) as DashboardDefinition
            if (onDashboardRestored(restoredDashboard, restoredVersion)) {
              setCurrentVersionId(restoredVersion.id)
              addToast(`Restored version from ${formatVersionTimestamp(restoredVersion.insertDate)}.`, 'success')
            } else {
              addToast('This saved version is no longer valid for the current Agent Builder catalog.', 'error')
            }
          } catch {
            addToast('This saved version is not valid dashboard JSON.', 'error')
          }
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to restore dashboard version.', 'error')
        },
        onSettled: () => {
          setRestoringVersionId(null)
        },
      },
    )
    setRestoringVersionId(version.id)
  }

  function applyVersionToDashboard(version: AgentBuilderDashboardVersion, successMessage: string) {
    const dashboardJson = version.dashboardJson
    if (!dashboardJson) {
      addToast('This version does not contain dashboard JSON.', 'warning')
      return
    }

    try {
      const restoredDashboard = JSON.parse(dashboardJson) as DashboardDefinition
      if (onDashboardRestored(restoredDashboard, version)) {
        setCurrentVersionId(version.id)
        addToast(successMessage, 'success')
      } else {
        addToast('This saved version is no longer valid for the current Agent Builder catalog.', 'error')
      }
    } catch {
      addToast('This saved version is not valid dashboard JSON.', 'error')
    }
  }

  function handlePublishVersion(version: AgentBuilderDashboardVersion) {
    publishMutation.mutate(
      {
        clientId,
        versionId: version.versionId || version.id,
      },
      {
        onSuccess: (publishedVersion) => {
          applyVersionToDashboard(
            publishedVersion,
            `Published version from ${formatVersionTimestamp(publishedVersion.insertDate)}.`,
          )
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to publish dashboard version.', 'error')
        },
        onSettled: () => {
          setPublishingVersionId(null)
        },
      },
    )
    setPublishingVersionId(version.id)
  }

  function handleUnpublishVersion(version: AgentBuilderDashboardVersion) {
    unpublishMutation.mutate(
      {
        clientId,
        versionId: version.versionId || version.id,
      },
      {
        onSuccess: (unpublishedVersion) => {
          if (currentVersionId === version.id || currentVersionId === unpublishedVersion.id) {
            setCurrentVersionId(null)
          }
          addToast(`Unpublished version from ${formatVersionTimestamp(unpublishedVersion.insertDate)}.`, 'success')
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to unpublish dashboard version.', 'error')
        },
        onSettled: () => {
          setUnpublishingVersionId(null)
        },
      },
    )
    setUnpublishingVersionId(version.id)
  }

  function handleDeleteVersion(version: AgentBuilderDashboardVersion) {
    deleteVersionMutation.mutate(
      {
        clientId,
        versionId: version.versionId || version.id,
        dashboardId: version.dashboardId ?? version.rootId ?? dashboardId,
        platformTypeId: version.platformTypeId,
      },
      {
        onSuccess: () => {
          addToast('Dashboard version deleted.', 'success')
        },
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to delete dashboard version.', 'error')
        },
        onSettled: () => {
          setDeletingVersionId(null)
        },
      },
    )
    setDeletingVersionId(version.id)
  }

  const chatTab = (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && !chatBusy && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3 py-12">
            <MessageOutlined className="text-4xl text-gray-300" />
            <div>
              <p className="text-sm font-medium">Ask me to modify or analyse your dashboard</p>
              <p className="text-xs mt-1 text-gray-400">
                e.g. "Change the bar chart to show by Partner" or "Which service line has the highest WIP?"
              </p>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.messageId}
            className={`flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[90%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'feature-button-solid text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>

            {msg.role === 'assistant' && msg.pendingScheduleConfirmation && msg.agentBuilderChatId && (
              <div className="max-w-full w-full mt-1">
                <ScheduleImpactConfirmCard
                  confirmation={msg.pendingScheduleConfirmation}
                  resolving={resolvingScheduleConfirmationId === msg.messageId}
                  canAccept={canModifyDashboards}
                  onResolve={(accept) => void handleScheduleConfirmation(msg.messageId, msg.agentBuilderChatId as string, accept)}
                />
              </div>
            )}

            {msg.role === 'assistant' && msg.computedResult && (
              <div className="max-w-full w-full mt-1">
                {msg.computedResult.rows && msg.computedResult.rows.length > 0 && msg.computedResult.columns ? (
                  <div className="overflow-x-auto rounded border border-blue-100 bg-blue-50">
                    <table className="text-xs border-collapse w-full">
                      <thead>
                        <tr className="bg-blue-100">
                          {msg.computedResult.columns.map((col) => (
                            <th key={col} className="px-2 py-1 text-left font-semibold text-blue-700 whitespace-nowrap">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {msg.computedResult.rows.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-blue-50/50'}>
                            {(msg.computedResult?.columns ?? []).map((col) => (
                              <td key={col} className="px-2 py-1 text-gray-700 whitespace-nowrap">
                                {formatChatMetricValue(row[col], col, resolveColumnFormat(msg.computedResult?.columnFormats, col))}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : msg.computedResult.value !== undefined && msg.computedResult.value !== null ? (
                  <div className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
                    <span className="text-xs font-medium text-blue-600">Result:</span>
                    <span className="text-sm font-semibold text-blue-800">{formatChatMetricValue(msg.computedResult.value, null, msg.computedResult.valueFormat)}</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}

        {chatBusy && (
          <div className="flex items-start gap-2">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2">
              <Spin size="small" />
              <span className="text-xs text-gray-500">Agent is thinking…</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex gap-2 items-end">
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question or request a dashboard change…"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={chatBusy || !clientId}
            className="flex-1 text-sm resize-none"
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={!input.trim() || chatBusy || !clientId}
            className="feature-button-solid flex-shrink-0"
          />
        </div>
      </div>
    </div>
  )


  const memories: AgentBuilderMemory[] = [...(memoriesQuery.data ?? [])].sort((a, b) => {
    const dashboardSpecificity = Number(b.dashboardScope === 'this_dashboard') - Number(a.dashboardScope === 'this_dashboard')
    if (dashboardSpecificity !== 0) return dashboardSpecificity
    const userSpecificity = Number(b.userScope === 'current_user') - Number(a.userScope === 'current_user')
    if (userSpecificity !== 0) return userSpecificity
    return new Date(b.updateDate ?? b.insertDate ?? 0).getTime() - new Date(a.updateDate ?? a.insertDate ?? 0).getTime()
  })
  const memoryBusy = createMemoryMutation.isPending || updateMemoryMutation.isPending || deleteMemoryMutation.isPending
  const memoryRefetching = memoriesQuery.isFetching && !memoriesQuery.isLoading
  const memoryDashboardOptions = [
    { label: 'All Dashboards', value: 'all_dashboards' as const },
    { label: 'This Dashboard Only', value: 'this_dashboard' as const, disabled: !rootDashboardId },
  ]
  const memoryUserScopeOptions = [
    { label: 'Only Me', value: 'current_user' as const },
    { label: 'Entire Client', value: 'entire_client' as const, disabled: !canManageClientMemory },
  ]
  const cannotAddMemory =
    !memoryInput.trim()
    || memoryBusy
    || (memoryDashboardScope === 'this_dashboard' && !rootDashboardId)
    || (memoryUserScope === 'entire_client' && !canManageClientMemory)

  function renderMemoryScopeTags(memory: AgentBuilderMemory) {
    return (
      <div className="mb-2 flex flex-wrap gap-1">
        <Tag color={memory.dashboardScope === 'this_dashboard' ? 'geekblue' : 'blue'} className="m-0 text-[11px]">
          {memory.dashboardScope === 'this_dashboard' ? 'This Dashboard Only' : 'All Dashboards'}
        </Tag>
        <Tag color={memory.userScope === 'entire_client' ? 'purple' : 'green'} className="m-0 text-[11px]">
          {memory.userScope === 'entire_client' ? 'Entire Client' : 'Only Me'}
        </Tag>
      </div>
    )
  }

  const memoryTab = (
    <div className="flex h-full flex-col">
      <>
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div className="mb-2 flex flex-wrap gap-2">
              <Select<AgentBuilderMemoryDashboardScope>
                size="small"
                options={memoryDashboardOptions}
                value={memoryDashboardScope}
                onChange={setMemoryDashboardScope}
                disabled={memoryBusy}
                className="min-w-[170px]"
                popupMatchSelectWidth={false}
              />
              <Tooltip title={canManageClientMemory ? '' : 'Only admins can manage client-wide memory.'}>
                <Select<AgentBuilderMemoryUserScope>
                  size="small"
                  options={memoryUserScopeOptions}
                  value={memoryUserScope}
                  onChange={setMemoryUserScope}
                  disabled={memoryBusy}
                  className="min-w-[140px]"
                  popupMatchSelectWidth={false}
                />
              </Tooltip>
            </div>
            <div className="flex items-end gap-2">
              <TextArea
                value={memoryInput}
                onChange={(e) => setMemoryInput(e.target.value)}
                placeholder="Add an internal note for agent responses..."
                autoSize={{ minRows: 2, maxRows: 5 }}
                disabled={memoryBusy}
                className="flex-1 text-sm resize-none"
              />
              <Tooltip title="Add memory">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateMemory}
                  disabled={cannotAddMemory}
                  loading={createMemoryMutation.isPending}
                  className="feature-button-solid flex-shrink-0"
                />
              </Tooltip>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {memoriesQuery.isLoading && (
              <div className="flex justify-center py-8">
                <Spin />
              </div>
            )}

            {memoryRefetching && (
              <div className="flex items-center gap-2 rounded-md border border-teal-100 bg-teal-50 px-3 py-2 text-xs text-teal-700">
                <Spin size="small" />
                <span>Refreshing memory...</span>
              </div>
            )}

            {memoriesQuery.isError && (
              <Empty
                description="Memory could not be loaded."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="py-8"
              />
            )}

            {!memoriesQuery.isLoading && !memoriesQuery.isError && memories.length === 0 && (
              <Empty
                description="No memories yet."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="py-8"
              />
            )}

            {memories.map((memory) => {
              const isEditing = editingMemoryId === memory.id
              const canManageMemory = memory.userScope === 'current_user' || canManageClientMemory
              return (
                <div key={memory.id} className="rounded-lg border border-gray-100 bg-white px-3 py-3 shadow-sm">
                  {renderMemoryScopeTags(memory)}
                  {isEditing ? (
                    <div className="space-y-2">
                      <TextArea
                        value={editingMemoryText}
                        onChange={(e) => setEditingMemoryText(e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        disabled={updateMemoryMutation.isPending}
                        className="text-sm resize-none"
                      />
                      <div className="flex justify-end gap-1">
                        <Tooltip title="Cancel">
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelEditMemory}
                            disabled={updateMemoryMutation.isPending}
                          />
                        </Tooltip>
                        <Tooltip title="Save">
                          <Button
                            type="text"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={() => handleUpdateMemory(memory)}
                            disabled={!editingMemoryText.trim() || updateMemoryMutation.isPending}
                            loading={updateMemoryMutation.isPending}
                            className="text-gray-500 hover:text-teal-600"
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <p className="mb-0 min-w-0 flex-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                        {memory.memory}
                      </p>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        <Tooltip title="Edit">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleStartEditMemory(memory)}
                            disabled={memoryBusy || !canManageMemory}
                            className="text-gray-400 hover:text-teal-600"
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete memory?"
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDeleteMemory(memory)}
                        >
                          <Tooltip title="Delete">
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              disabled={memoryBusy || !canManageMemory}
                              loading={deleteMemoryMutation.isPending}
                              className="text-gray-400 hover:text-red-500"
                            />
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
    </div>
  )


  const versions: AgentBuilderDashboardVersion[] = versionsQuery.data ?? []

  const versionHistoryTab = (
    <div className="flex flex-col h-full overflow-y-auto px-4 py-4 space-y-3">
      {versionsQuery.isLoading && (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      )}

      {!versionsQuery.isLoading && versions.length === 0 && (
        <Empty
          description="No versions yet - ask the agent to modify the dashboard."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="py-8"
        />
      )}

      {versions.map((version) => {
        const isCurrent = version.isCurrent || version.id === currentVersionId
        const canPublish = isDiAdmin && version.isDraft
        const canUnpublish = isDiAdmin && !version.isDraft
        const canDelete = canManageClientMemory && canModifyDashboards && !isCurrent
        const canRestore = isDiAdmin
        const versionActionDisabled = restoreMutation.isPending
          || publishMutation.isPending
          || unpublishMutation.isPending
          || deleteVersionMutation.isPending
        return (
          <div
            key={version.id}
            className={`flex items-start gap-3 rounded-lg border px-3 py-3 shadow-sm ${
              isCurrent ? 'border-teal-200 bg-teal-50/60' : 'border-gray-100 bg-white'
            }`}
          >
            <HistoryOutlined className="mt-0.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <p className="mb-0 text-xs font-medium text-gray-500">{formatVersionTimestamp(version.insertDate)}</p>
                {isCurrent && <Tag color="green" className="!mr-0">Current</Tag>}
                <Tag color={version.isDraft ? 'gold' : 'blue'} className="!mr-0">
                  {version.isDraft ? 'Draft' : 'Published'}
                </Tag>
              </div>
              <p className="mb-0 line-clamp-3 text-sm text-gray-700 leading-snug">
                {version.prompt || 'Dashboard update'}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              {canRestore && (
                <Tooltip title="Restore">
                  <Button
                    type="text"
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={() => handleRestoreVersion(version)}
                    disabled={versionActionDisabled}
                    loading={restoreMutation.isPending && version.id === restoringVersionId}
                    className="text-gray-400 hover:text-teal-600"
                  />
                </Tooltip>
              )}
              {canPublish && (
                <Tooltip title="Publish">
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handlePublishVersion(version)}
                    disabled={versionActionDisabled}
                    loading={publishMutation.isPending && version.id === publishingVersionId}
                    className="text-gray-400 hover:text-green-600"
                  />
                </Tooltip>
              )}
              {canUnpublish && (
                <Tooltip title="Unpublish">
                  <Button
                    type="text"
                    size="small"
                    icon={<StopOutlined />}
                    onClick={() => handleUnpublishVersion(version)}
                    disabled={versionActionDisabled}
                    loading={unpublishMutation.isPending && version.id === unpublishingVersionId}
                    className="text-gray-400 hover:text-amber-600"
                  />
                </Tooltip>
              )}
              {canDelete && (
                <Popconfirm
                  title="Delete version?"
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDeleteVersion(version)}
                >
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={versionActionDisabled}
                      loading={deleteVersionMutation.isPending && version.id === deletingVersionId}
                      className="text-gray-400 hover:text-red-500"
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )

  const settingsContent = (
    <div className="flex min-w-[250px] flex-col gap-1" role="menu" aria-label="Chat settings">
      {isDiAdmin && (
        <div className="flex items-center justify-between gap-4 rounded-md px-3 py-2" role="menuitem">
          <span className="text-sm text-gray-700">Draft Dashboard Modification</span>
          <Switch
            size="small"
            checked={saveChatModificationsAsDraft}
            onChange={setSaveChatModificationsAsDraft}
            disabled={chatBusy}
            aria-label="Draft Dashboard Modification"
          />
        </div>
      )}
      <Button
        type="text"
        icon={<DeleteOutlined />}
        onClick={handleClear}
        className="!flex w-full !justify-start"
        role="menuitem"
      >
        Reset Chat
      </Button>
      <Button
        type="text"
        icon={<BulbOutlined />}
        onClick={() => openChildView('memory')}
        className="!flex w-full !justify-start"
        role="menuitem"
      >
        Memory
      </Button>
      <Button
        type="text"
        icon={<HistoryOutlined />}
        onClick={() => openChildView('version-history')}
        className="!flex w-full !justify-start"
        role="menuitem"
      >
        Version History
      </Button>
    </div>
  )

  const childView = activeChildView === 'memory'
    ? { title: 'Memory', icon: <BulbOutlined />, content: memoryTab }
    : { title: 'Version History', icon: <HistoryOutlined />, content: versionHistoryTab }

  return (
    <div className="relative flex flex-col h-full bg-white">
      {onExpandedChange && (
        <button
          onClick={() => onExpandedChange(!expanded)}
          className="absolute -left-5 top-1/2 z-10 flex h-16 w-5 -translate-y-1/2 items-center justify-center rounded-l-md border border-r-0 border-gray-200 bg-white text-gray-400 shadow-md transition-colors hover:bg-gray-50"
          title={expanded ? 'Collapse panel' : 'Expand panel'}
          aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
          type="button"
        >
          {expanded ? <DoubleRightOutlined className="text-xs" /> : <DoubleLeftOutlined className="text-xs" />}
        </button>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageOutlined className="feature-icon-accent text-lg" />
          <span className="font-semibold text-gray-800 text-sm">Chat</span>
        </div>
        <div className="flex items-center gap-1">
          <Popover
            content={settingsContent}
            trigger="click"
            placement="bottomRight"
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
          >
            <Tooltip title="Settings">
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Chat settings"
              />
            </Tooltip>
          </Popover>
          {onClose && (
            <Tooltip title="Minimize">
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                onClick={onClose}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Minimize Chat"
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1">{chatTab}</div>

      <div
        aria-hidden={!childViewOpen}
        data-state={childViewOpen ? 'open' : 'closed'}
        data-testid="agent-builder-child-sidebar"
        {...(!childViewOpen ? { inert: '' } : {})}
        className={`absolute inset-0 z-20 flex flex-col bg-white transition-transform duration-300 ease-in-out motion-reduce:transition-none ${
          childViewOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Tooltip title="Back to Chat">
              <Button
                type="text"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={() => setChildViewOpen(false)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Back to Chat"
              />
            </Tooltip>
            <span className="feature-icon-accent flex items-center text-lg">{childView.icon}</span>
            <span className="text-sm font-semibold text-gray-800">{childView.title}</span>
          </div>
        </div>
        <div className="min-h-0 flex-1">{childView.content}</div>
      </div>
    </div>
  )
}
